import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Button from '@material-ui/core/Button'
import sortBy from 'lodash/sortBy'
import Dialog from "@material-ui/core/Dialog";
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { v4 as uuidv4 } from 'uuid';

import { Flex, PromptHeader, PromptBody, PromptButton, PrimaryHeader } from '../styles/global'
import { getSimulations, deleteSimulation, getQuizzes, getSurveys } from '../utils/api.utils'
import { SimulationEditor } from './SimulationEditor'
import { Simulation } from '../models'
import { useIsComponentMounted } from '../utils/hooks.utils'
import { bucketSimulation, parseSimWallClock } from '../utils/ui.utils'

import defaultSimulation from '../data/defaultSimulation'
import simulationIcon from "../assets/simulation.svg";

export const SimulationList = () => {

  const [filter, setFilter] = useState<string>('active')
  const [showEditScreen, setEditScreen] = useState(false)
  const [loading, setLoading] = useState(true)

  const [currentSimulation, setCurrentSimulation] = useState<any>()
  const [simulations, setSimulations] = useState<Simulation[]>([])

  const isComponentMounted = useIsComponentMounted();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneLoading, setCloneLoading] = useState(false);

  const fetchSimulations = () => {
    // eslint-disable-next-line no-unused-expressions
    getSimulations()
      .then((result) => {
        if (isComponentMounted.current) {
          setLoading(false)
          const payload = result as any
          // DEBUG: Uncomment below to debug simulation list fetching issues
          // console.log("DEBUG fetchSimulations: Raw payload from server:", payload);
          // if (payload && payload.length > 0) {
          //   payload.forEach((sim: any) => {
          //     if (sim.code === 'dtest') {
          //       console.log("DEBUG fetchSimulations: DEV TEST SIM is_modified =", sim.is_modified, "pathogen =", sim.pathogen);
          //     }
          //   });
          // }
          setSimulations(sortBy(payload, 'created_at'))
        }
      })
      .catch((err) => {
        if (isComponentMounted.current) {
          setSimulations([])
          setLoading(false)
        }
      })
  }

  const createSimulation = () => {
    setCurrentSimulation({
      ...defaultSimulation,
      uuid: uuidv4() // Generate a fresh UUID for each new simulation
    })
    setEditScreen(true)
  }

  const selectSimulation = (simulation: any) => {
    setCurrentSimulation(simulation)
    setEditScreen(true)
  }

  const handleSimulationDelete = (simulation: any) => {
    deleteSimulation(simulation.id)
      .then((res) => console.log(res))
      .catch((err) => console.error(err));
    setCurrentSimulation(undefined);
    setShowDeleteModal(false);
    setTimeout(() => fetchSimulations(), 1000);
    fetchSimulations();
  };

  const displayDeleteModal = (simulation: any) => {
    if (!simulation) {
      return;
    }
    setCurrentSimulation(simulation);
    setShowDeleteModal(true);
  };

  const displayCloneModal = (simulation: any) => {
    if (!simulation) return;
    setCurrentSimulation(simulation);
    setShowCloneModal(true);
  };

  const handleSimulationClone = (simulation: any) => {
    setCloneLoading(true);
    setShowCloneModal(false);

    // Fetch the original sim's quizzes and surveys so they can be pre-populated
    // in the editor as local state — no data is written to the backend until the user saves.
    Promise.all([
      getQuizzes(simulation.code).catch(() => []),
      getSurveys(simulation.code).catch(() => []),
    ]).then(([quizzes, surveys]: any) => {
      // Strip all server-assigned fields so the backend treats this as a brand-new simulation
      const {
        id, uuid,
        created_at, updated_at,
        active_participants, infected_participants,
        active_simulation_id,
        active_participants_count, infected_participants_count,
        is_active, archived,
        pathogen_name,
        ...simRest
      } = simulation;

      const clonedSim = {
        ...simRest,
        uuid: uuidv4(), // Generate a new UUID for the cloned simulation
        name: `${simulation.name} (Copy)`,
        code: `${simulation.code}copy`,
        archived: false,
        // Carry quizzes and surveys as in-memory seed data
        _seedQuizzes: (quizzes || []).map((quiz: any) => ({
          ...quiz,
          id: undefined,
          quiz_id: undefined,
          sim_id: undefined,
          created_at: undefined,
          locked: false,
          questions: (quiz.questions || []).map((q: any) => ({
            ...q,
            id: undefined,
            created_at: undefined,
            quiz_id: undefined,
          })),
        })),
        _seedSurveys: (surveys || []).map((survey: any) => ({
          ...survey,
          id: undefined,
          survey_id: undefined,
          sim_id: undefined,
          created_at: undefined,
          locked: false,
          questions: (survey.questions || []).map((q: any) => ({
            ...q,
            id: undefined,
            created_at: undefined,
            survey_id: undefined,
          })),
        })),
      };

      setCloneLoading(false);
      setCurrentSimulation(clonedSim);
      setEditScreen(true);
    }).catch((err: any) => {
      console.error(err);
      setCloneLoading(false);
    });
  };

  useEffect(() => {
    if (isComponentMounted.current) {
      fetchSimulations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const visibleSimulations = simulations.filter((sim: any) => bucketSimulation(sim) === filter);

  if (showEditScreen) {
    window.scrollTo(0, 0)
    // Extract seed data attached during cloning (not real server fields)
    const { _seedQuizzes, _seedSurveys, ...simForEditor } = currentSimulation || {};
    return (
      <SimulationEditor
        shouldDisplay={setEditScreen}
        fetchSimulations={fetchSimulations}
        simulation={simForEditor}
        initialQuizzes={_seedQuizzes}
        initialSurveys={_seedSurveys}
      />
    )
  }
  return (
    <div style={{ padding: '0 0 24px 0', width: '100%', boxSizing: 'border-box' }}>
      <Flex justify="space-between" alignItems="center" style={{ marginBottom: '24px' }}>
        <PrimaryHeader style={{ margin: 0 }}>Simulations</PrimaryHeader>
        <Button
          variant="contained"
          color="primary"
          style={{ borderRadius: '8px', padding: '10px 24px', fontWeight: 600, boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)' }}
          onClick={() => createSimulation()}>
          + New Simulation
        </Button>
      </Flex>

      <ThemedTabs
        value={filter === 'active' ? 0 : filter === 'future' ? 1 : filter === 'past' ? 2 : 3}
        onChange={(e: React.ChangeEvent<{}>, newValue: number) => {
          const filters = ['active', 'future', 'past', 'archived'];
          setFilter(filters[newValue]);
        }}
        aria-label="simulation filters"
        TabIndicatorProps={{ style: { backgroundColor: '#3f51b5', height: '3px' } }}
      >
        <Tab label="Active" />
        <Tab label="Future" />
        <Tab label="Past" />
        <Tab label="Archived" />
      </ThemedTabs>

      <div id="simulations-view">
        {!loading
          ? visibleSimulations && visibleSimulations.length >= 1
            ? visibleSimulations.map((item: any) => (
              <SimulationCard
                key={item.id + item.name}
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <SimulationInfoContainer direction="row" alignItems="center" style={{ flex: 1 }}>
                  <IconContainer>
                    <img src={simulationIcon} alt="Simulation" />
                  </IconContainer>
                  <SimulationTextContent direction="column">
                    <Flex alignItems="center" style={{ marginBottom: '8px' }}>
                      <SimulationTitle>{item.name || "Untitled Simulation"}</SimulationTitle>
                      {item.pathogen_name && (
                        <StatusPill className="pathogen">{item.pathogen_name}</StatusPill>
                      )}
                    </Flex>
                    <Flex wrap="wrap" alignItems="center">
                      {item.venue && <StatPill>{item.venue}</StatPill>}
                      {item.size && <StatPill>{item.size} Users</StatPill>}
                      <StatPill>ID: {item.id}</StatPill>
                      {item.end_date && (
                        <TimestampText>
                          Ends {parseSimWallClock(item, item.end_date)?.format('LL')}
                        </TimestampText>
                      )}
                    </Flex>
                  </SimulationTextContent>
                </SimulationInfoContainer>

                <ActionGroup alignItems="center">
                  <ActionButton
                    variant="outlined"
                    onClick={() => selectSimulation(item)}
                  >
                    Manage
                  </ActionButton>
                  <CloneButton
                    variant="outlined"
                    disabled={cloneLoading}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      (e.currentTarget as HTMLElement).blur();
                      displayCloneModal(item);
                    }}
                  >
                    {cloneLoading && currentSimulation?.id === item.id ? 'Loading…' : 'Clone'}
                  </CloneButton>
                  <DeleteButton onClick={(e) => {
                    (e.currentTarget as HTMLElement).blur();
                    displayDeleteModal(item);
                  }}>
                    Delete
                  </DeleteButton>
                </ActionGroup>
              </SimulationCard>
            ))
            : <NotFound>No Simulations Found</NotFound>
          : <NotFound>Loading...</NotFound>}

        <Dialog
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          PaperProps={{
            style: { borderRadius: '16px', padding: '8px', maxWidth: '450px' }
          }}
        >
          <ModalContent direction="column" alignItems="center">
            <div className="icon-container delete">!</div>
            <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
              Delete Simulation?
            </PromptHeader>
            <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
              Are you sure you want to delete this simulation? All participant data and session history will be permanently lost.
            </PromptBody>
            <Flex style={{ marginTop: '32px', width: '100%', gap: '12px' }} justify="center">
              <PromptButton
                autoFocus
                onClick={() => setShowDeleteModal(false)}
                variant="outlined"
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  borderColor: '#e2e8f0'
                }}
              >
                Cancel
              </PromptButton>
              <PromptButton
                onClick={() => handleSimulationDelete(currentSimulation)}
                variant="contained"
                style={{
                  background: '#c10000',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  boxShadow: '0 4px 12px rgba(193, 0, 0, 0.2)'
                }}
              >
                Delete
              </PromptButton>
            </Flex>
          </ModalContent>
        </Dialog>
        <Dialog
          open={showCloneModal}
          onClose={() => setShowCloneModal(false)}
          PaperProps={{
            style: { borderRadius: '16px', padding: '8px', maxWidth: '450px' }
          }}
        >
          <ModalContent direction="column" alignItems="center">
            <div className="icon-container clone">⧉</div>
            <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
              Clone Simulation?
            </PromptHeader>
            <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
              A new simulation will be created with the same settings, quizzes, and surveys as <strong>{currentSimulation?.name}</strong>. You can then update the name, join code, and dates before saving.
            </PromptBody>
            <Flex style={{ marginTop: '32px', width: '100%', gap: '12px' }} justify="center">
              <PromptButton
                autoFocus
                onClick={() => setShowCloneModal(false)}
                variant="outlined"
                style={{
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  borderColor: '#e2e8f0'
                }}
              >
                Cancel
              </PromptButton>
              <PromptButton
                onClick={() => handleSimulationClone(currentSimulation)}
                variant="contained"
                style={{
                  background: '#3f51b5',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: 1,
                  boxShadow: '0 4px 12px rgba(63, 81, 181, 0.2)'
                }}
              >
                Clone
              </PromptButton>
            </Flex>
          </ModalContent>
        </Dialog>
      </div>
    </div>
  )
}

const ModalContent = styled(Flex)`
  padding: 32px;
  
  .icon-container {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 20px;
    
    &.delete {
      background: #fff5f5;
      color: #e53e3e;
      border: 2px solid #feb2b2;
    }

    &.clone {
      background: #ebf4ff;
      color: #3f51b5;
      border: 2px solid #c5cae9;
      font-size: 20px;
    }
  }
`;

const SimulationCard = styled(Flex)`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.15);
    border-color: #cbd5e0;
  }
`;

const IconContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 24px;
  flex-shrink: 0;
  border: 1px solid #edf2f7;

  img {
    width: 32px;
    height: 32px;
    opacity: 0.8;
  }
`;

const SimulationInfoContainer = styled(Flex)``;

const SimulationTextContent = styled(Flex)`
  flex: 1;
`;

const SimulationTitle = styled.h4`
  font-size: 22px;
  font-weight: 800;
  color: #1a202c;
  margin: 0;
  margin-right: 16px;
`;

const StatusPill = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.05em;
  
  &.pathogen {
    background: #ebf8ff;
    color: #2b6cb0;
    border: 1px solid #bee3f8;
  }
`;

const StatPill = styled.span`
  font-size: 13px;
  color: #4a5568;
  background: #f7fafc;
  padding: 4px 12px;
  border-radius: 20px;
  margin-right: 12px;
  border: 1px solid #edf2f7;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimestampText = styled.span`
  font-size: 13px;
  color: #a0aec0;
  font-weight: 500;
`;

const ActionGroup = styled(Flex)`
  margin-left: 24px;
  gap: 12px;
`;

const ActionButton = styled(Button)`
  && {
    min-width: 120px;
    height: 44px;
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    border-color: #e2e8f0;
    color: #2d3748;
    background: white;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    &:hover {
      background: #f7fafc;
      border-color: #cbd5e0;
    }
  }
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 80px;
    height: 44px;
    text-transform: none;
    font-weight: 600;
    color: #e53e3e;
    border-radius: 10px;
    &:hover {
      background: #fff5f5;
    }
  }
`;

const CloneButton = styled(Button)`
  && {
    min-width: 90px;
    height: 44px;
    text-transform: none;
    font-weight: 600;
    color: #3f51b5;
    border-radius: 10px;
    border-color: #c5cae9;
    &:hover {
      background: #e8eaf6;
      border-color: #9fa8da;
    }
    &.Mui-disabled {
      color: #a0aec0;
    }
  }
`;

const ThemedTabs = styled((props: any) => <Tabs {...props} />)`
  margin-bottom: 32px;
  border-bottom: 1px solid #e2e8f0;
  .MuiTab-root {
    text-transform: uppercase;
    font-weight: 700;
    font-size: 13px;
    color: #718096;
    min-width: 120px;
    letter-spacing: 0.05em;
    &.Mui-selected {
      color: #3f51b5;
    }
  }
`;

const NotFound = styled.h4`
  font-weight: 600;
  margin-top: 100px;
  font-size: 22px;
  color: #a0aec0;
  text-align: center;
`;